import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "../lib/api";
import { Est } from "../lib/currency";
import { Button } from "./ui/button";
import { BuyConfirmDialog } from "./BidzPackages";
import { Coins, AlertTriangle, Ticket } from "lucide-react";
import { toast } from "sonner";

export const PASS_TERMS = [
  "Gives bidding access to this ONE auction only. No Bidz are added to your wallet.",
  "Place as many bids as you like on this auction with no Bidz deducted.",
  "The pass expires when this auction ends and cannot be transferred to another auction.",
  "If AuctionBulls cancels or invalidates this auction, you receive a replacement pass credit.",
  "Bidding on several auctions? Bidz packages (Starter, Premium, Pro) are better value.",
];

function PassOffer({ bidz, listing, onBought }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const redeem = async () => {
    try { await api.post("/bidz/pass/redeem", { listing_id: listing.id }); toast.success("Auction Pass activated with your pass credit"); onBought(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not use pass credit"); }
  };
  if (!bidz.pass_enabled) return null;
  return (
    <div className="rounded-lg bg-white/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 p-3 space-y-2" data-testid="bid-pass-offer">
      <div className="flex items-center gap-2 font-semibold"><Ticket className="h-4 w-4 text-amber-600" /> One-Time Auction Pass · {money(bidz.pass_price)} <Est inr={bidz.pass_price} /></div>
      <div className="text-xs text-slate-500">Unlimited bids on this auction only. No Bidz used.</div>
      {confirming && bidz.pass_credit_value > bidz.pass_price && (
        <div className="text-xs text-amber-700 dark:text-amber-400" data-testid="bid-redeem-credit-note">Your {money(bidz.pass_credit_value)} credit covers this {money(bidz.pass_price)} pass. The remaining {money(bidz.pass_credit_value - bidz.pass_price)} is not carried over.</div>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        {bidz.pass_credit_available
          ? (confirming
            ? <Button size="sm" onClick={redeem} data-testid="bid-redeem-pass-credit-confirm" className="bg-emerald-600 hover:bg-emerald-500 text-white flex-1">Confirm: use {money(bidz.pass_credit_value)} credit</Button>
            : <Button size="sm" onClick={() => setConfirming(true)} data-testid="bid-redeem-pass-credit-btn" className="bg-emerald-600 hover:bg-emerald-500 text-white flex-1">Use my replacement pass credit</Button>)
          : <Button size="sm" onClick={() => setOpen(true)} data-testid="bid-buy-pass-btn" className="bg-slate-900 text-white flex-1">Buy Auction Pass · {money(bidz.pass_price)}</Button>}
        <Link to="/bidz" className="flex-1"><Button size="sm" variant="outline" className="w-full" data-testid="bid-see-packages-btn">See Bidz packages</Button></Link>
      </div>
      <BuyConfirmDialog product={open ? { kind: "pass", name: "One-Time Auction Pass", price: bidz.pass_price, currency: "INR" } : null}
        listing={listing} terms={PASS_TERMS} onClose={() => setOpen(false)} onBought={() => { setOpen(false); onBought(); }} />
    </div>
  );
}

export function BidzBidSection({ bidz, listing, onBought }) {
  if (!bidz) return null;
  if (bidz.has_pass) {
    return (
      <div className="rounded-xl border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-800 p-4 text-sm" data-testid="bid-pass-active">
        <div className="font-bold flex items-center gap-2"><Ticket className="h-4 w-4 text-emerald-600" /> Auction Pass active</div>
        <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">Unlimited bids on this auction. No Bidz will be deducted.</div>
      </div>
    );
  }
  if (!bidz.enabled) return null;
  const known = bidz.general_available !== undefined;
  return (
    <div className="rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/20 p-4 space-y-2 text-sm" data-testid="bid-bidz-section">
      <div className="flex justify-between items-center font-bold"><span className="flex items-center gap-2"><Coins className="h-4 w-4 text-amber-600" /> Bidz for this bid</span><span data-testid="bid-bidz-cost">{bidz.cost} Bidz</span></div>
      {known && (
        <>
          <div className="flex justify-between text-slate-600 dark:text-slate-400"><span>Wallet balance</span><span data-testid="bid-bidz-available">{bidz.general_available}</span></div>
          {bidz.sufficient && <div className="flex justify-between text-slate-600 dark:text-slate-400"><span>Balance after bid</span><span data-testid="bid-bidz-after">{bidz.general_available - bidz.cost}</span></div>}
        </>
      )}
      <div className="text-[11px] text-slate-500">Deducted only if your bid is accepted. Not returned if you're outbid. Separate from the item payment.</div>
      {bidz.sufficient === false && (
        <div className="flex gap-2 text-red-700 dark:text-red-400 text-xs font-semibold pt-1" data-testid="bid-bidz-insufficient">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" /> You need {bidz.cost - bidz.general_available} more Bidz — or bid with an Auction Pass.
        </div>
      )}
      {known && <PassOffer bidz={bidz} listing={listing} onBought={onBought} />}
    </div>
  );
}
